import { ReactNode, useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList,
} from "recharts";
import { IExpense } from "../models/IExpense";
import { getExchangeRates, resolveTryRate, IExchangeRateSnapshot } from "../services/exchangeRate";

// dataviz skill — validated categorical palette (references/palette.md)
const PALETTE = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];
const TRY_COLOR = PALETTE[0];
// Common symbols get a fixed slot first, so the same currency always gets the same color.
const CURRENCY_PRIORITY = ["₺", "$", "€", "£"];

const MAX_VENDOR_ROWS = 8;

function truncate(text: string, max: number): string {
  return text.length > max ? text.slice(0, max - 1) + "…" : text;
}

function formatAmount(n: number): string {
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function capVendors(vendors: { name: string; amount: number }[]): { name: string; amount: number }[] {
  const sorted = [...vendors].sort((a, b) => b.amount - a.amount);
  if (sorted.length <= MAX_VENDOR_ROWS) return sorted;
  const top = sorted.slice(0, MAX_VENDOR_ROWS - 1);
  const rest = sorted.slice(MAX_VENDOR_ROWS - 1).reduce((s, v) => s + v.amount, 0);
  return [...top, { name: "Diğer", amount: rest }];
}

interface CurrencyGroup {
  currencyId: string;
  currencyDisplayText: string;
  total: number;
  count: number;
}

function buildCurrencyGroups(expenses: IExpense[]): CurrencyGroup[] {
  const byCurrency = new Map<string, IExpense[]>();
  expenses.forEach((e) => {
    const list = byCurrency.get(e.currencyId) ?? [];
    list.push(e);
    byCurrency.set(e.currencyId, list);
  });
  return Array.from(byCurrency.entries()).map(([currencyId, items]) => ({
    currencyId,
    currencyDisplayText: items[0].currencyDisplayText,
    total: items.reduce((s, e) => s + e.amount, 0),
    count: items.length,
  })).sort((a, b) => b.total - a.total);
}

function vendorKey(e: IExpense): string {
  return e.vendorName?.trim() || "Belirtilmemiş";
}

function assignCurrencyColors(displayTexts: string[]): Record<string, string> {
  const ordered = [
    ...CURRENCY_PRIORITY.filter((k) => displayTexts.includes(k)),
    ...displayTexts.filter((k) => !CURRENCY_PRIORITY.includes(k)),
  ];
  const map: Record<string, string> = {};
  ordered.forEach((k, i) => { map[k] = PALETTE[i % PALETTE.length]; });
  return map;
}

function tooltipFormatter(currencyDisplayText: string) {
  return (val: number | undefined) => [`${formatAmount(val ?? 0)} ${currencyDisplayText}`, "Tutar"];
}

function labelFormatter(v: ReactNode) {
  return Number(v).toLocaleString("tr-TR", { maximumFractionDigits: 0 });
}

function VendorBarChart({ vendors, color, currencyDisplayText }: { vendors: { name: string; amount: number }[]; color: string; currencyDisplayText: string }) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(120, vendors.length * 40)}>
      <BarChart data={vendors} layout="vertical" margin={{ top: 4, right: 36, bottom: 4, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fill: "#898781" }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v: number) => v.toLocaleString("tr-TR")}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={110}
          tick={{ fontSize: 12, fontWeight: 600, fill: "#374151" }}
          tickFormatter={(v: string) => truncate(v, 16)}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          cursor={{ fill: "#f9fafb" }}
          formatter={tooltipFormatter(currencyDisplayText)}
          contentStyle={{ borderRadius: "10px", border: "1px solid #e5e7eb", fontSize: "0.82rem" }}
        />
        <Bar dataKey="amount" fill={color} radius={[0, 4, 4, 0]} barSize={22}>
          <LabelList dataKey="amount" position="right" formatter={labelFormatter} style={{ fontSize: 11, fill: "#52514e", fontWeight: 600 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function ExpenseSummary({ expenses }: { expenses: IExpense[] }) {
  const [rates, setRates] = useState<IExchangeRateSnapshot | null>(null);
  const [ratesError, setRatesError] = useState(false);
  const [ratesLoading, setRatesLoading] = useState(true);

  useEffect(() => {
    getExchangeRates()
      .then(setRates)
      .catch(() => setRatesError(true))
      .finally(() => setRatesLoading(false));
  }, []);

  if (expenses.length === 0) return null;

  const currencyGroups = buildCurrencyGroups(expenses);
  const colorByCurrency = assignCurrencyColors(currencyGroups.map((g) => g.currencyDisplayText));

  // ── Kur bilgisi varsa: tek para birimine (TL) çevrilmiş birleşik görünüm ──
  if (rates) {
    const convertible: { expense: IExpense; tryAmount: number }[] = [];
    const unconvertibleCount = { current: 0 };
    expenses.forEach((e) => {
      const rate = resolveTryRate(e.currencyDisplayText, e.currencyName, rates);
      if (rate === null) { unconvertibleCount.current++; return; }
      convertible.push({ expense: e, tryAmount: e.amount * rate });
    });

    const totalTry = convertible.reduce((s, c) => s + c.tryAmount, 0);
    const vendorMap = new Map<string, number>();
    convertible.forEach(({ expense, tryAmount }) => {
      const key = vendorKey(expense);
      vendorMap.set(key, (vendorMap.get(key) ?? 0) + tryAmount);
    });
    const vendors = capVendors(Array.from(vendorMap, ([name, amount]) => ({ name, amount })));

    return (
      <div className="card mb-4" style={{ borderRadius: "14px", border: "1.5px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <div className="fw-semibold" style={{ color: "#374151", fontSize: "0.82rem" }}>
              <i className="bi bi-bar-chart-fill me-2 text-primary"></i>
              HARCAMA ÖZETİ (TL KARŞILIĞI)
            </div>
            <div className="text-muted" style={{ fontSize: "0.7rem" }}>
              <i className="bi bi-arrow-repeat me-1"></i>
              Kur: {rates.date} {rates.time} · $ {formatAmount(rates.usdBuy)} · € {formatAmount(rates.eurBuy)} · £ {formatAmount(rates.gbpBuy)}
            </div>
          </div>

          <div className="row g-3 mb-4">
            {/* Birleşik toplam */}
            <div className="col-12 col-md-4">
              <div className="h-100 p-3 rounded-3" style={{ background: `${TRY_COLOR}14`, border: `1.5px solid ${TRY_COLOR}40` }}>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: TRY_COLOR, flexShrink: 0 }} />
                  <span className="text-muted fw-semibold" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Toplam Harcama
                  </span>
                </div>
                <div className="fw-bold" style={{ fontSize: "1.6rem", color: TRY_COLOR, lineHeight: 1.2 }}>
                  {formatAmount(totalTry)} ₺
                </div>
                <div className="mt-1 text-muted" style={{ fontSize: "0.76rem" }}>{convertible.length} fatura</div>
              </div>
            </div>
            {/* Orijinal para birimine göre küçük referans rozetleri */}
            <div className="col-12 col-md-8 d-flex flex-wrap align-content-start gap-2">
              {currencyGroups.map((g) => (
                <div key={g.currencyId} className="d-flex align-items-center gap-2 px-3 py-2 rounded-3"
                  style={{ background: "#f9fafb", border: "1px solid #e5e7eb" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: colorByCurrency[g.currencyDisplayText], flexShrink: 0 }} />
                  <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#374151" }}>
                    {formatAmount(g.total)} {g.currencyDisplayText}
                  </span>
                  <span className="text-muted" style={{ fontSize: "0.72rem" }}>{g.count} fatura</span>
                </div>
              ))}
              {unconvertibleCount.current > 0 && (
                <div className="d-flex align-items-center gap-2 px-3 py-2 rounded-3" style={{ background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", fontSize: "0.76rem" }}>
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  {unconvertibleCount.current} fatura kur bulunamadığı için toplama dahil edilmedi
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="fw-semibold mb-2" style={{ color: "#374151", fontSize: "0.8rem" }}>
              <i className="bi bi-building me-2" style={{ color: TRY_COLOR }}></i>
              FİRMA BAZLI HARCAMA (TL KARŞILIĞI)
            </div>
            <div className="p-3 rounded-3" style={{ background: "#fff", border: "1.5px solid #e5e7eb" }}>
              <VendorBarChart vendors={vendors} color={TRY_COLOR} currencyDisplayText="₺" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Kur yüklenene kadar / kur alınamadıysa: para birimine göre ayrı ayrı göster ──
  const groupsWithVendors = currencyGroups.map((g) => {
    const items = expenses.filter((e) => e.currencyId === g.currencyId);
    const vendorMap = new Map<string, number>();
    items.forEach((e) => vendorMap.set(vendorKey(e), (vendorMap.get(vendorKey(e)) ?? 0) + e.amount));
    return { ...g, vendors: capVendors(Array.from(vendorMap, ([name, amount]) => ({ name, amount }))) };
  });

  return (
    <div className="card mb-4" style={{ borderRadius: "14px", border: "1.5px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
      <div className="card-body">
        <div className="fw-semibold mb-2" style={{ color: "#374151", fontSize: "0.82rem" }}>
          <i className="bi bi-bar-chart-fill me-2 text-primary"></i>
          HARCAMA ÖZETİ
        </div>
        {ratesLoading && (
          <div className="text-muted mb-3" style={{ fontSize: "0.78rem" }}>
            <span className="spinner-border spinner-border-sm me-2" /> Kur bilgisi yükleniyor…
          </div>
        )}
        {ratesError && (
          <div className="mb-3 px-3 py-2 rounded-3" style={{ background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", fontSize: "0.78rem" }}>
            <i className="bi bi-exclamation-triangle-fill me-1"></i>
            Kur bilgisi alınamadı, para birimleri ayrı ayrı gösteriliyor.
          </div>
        )}

        <div className="row g-3 mb-4">
          {groupsWithVendors.map((g) => {
            const color = colorByCurrency[g.currencyDisplayText];
            return (
              <div key={g.currencyId} className="col-6 col-md-4 col-lg-3">
                <div className="h-100 p-3 rounded-3" style={{ background: `${color}14`, border: `1.5px solid ${color}40` }}>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                    <span className="text-muted fw-semibold" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Toplam ({g.currencyDisplayText})
                    </span>
                  </div>
                  <div className="fw-bold" style={{ fontSize: "1.4rem", color, lineHeight: 1.2 }}>
                    {formatAmount(g.total)} {g.currencyDisplayText}
                  </div>
                  <div className="mt-1 text-muted" style={{ fontSize: "0.76rem" }}>{g.count} fatura</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="row g-3">
          {groupsWithVendors.map((g) => {
            const color = colorByCurrency[g.currencyDisplayText];
            return (
              <div key={g.currencyId} className="col-12 col-lg-6">
                <div className="p-3 rounded-3 h-100" style={{ background: "#fff", border: "1.5px solid #e5e7eb" }}>
                  <div className="fw-semibold mb-2" style={{ color: "#374151", fontSize: "0.8rem" }}>
                    <i className="bi bi-building me-2" style={{ color }}></i>
                    FİRMA BAZLI HARCAMA — {g.currencyDisplayText}
                  </div>
                  <VendorBarChart vendors={g.vendors} color={color} currencyDisplayText={g.currencyDisplayText} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
